import { renderToStaticMarkup } from 'react-dom/server';
import React from 'react';
import { ReportPageType, ReportSection, ReportSettingsFrequency } from '../constants/common';
import ReportService from '../service/ReportService';
import Utils from './Utils';
import BRSRSectionA from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRSectionA';
// import BRSRSectionB from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRSectionB';
import BRSRSectionBGridLayout from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRSectionBGridLayout';
import BRSRSectionC from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRSectionC';
import BRSRPrinciple1 from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRPrinciple1';
import BRSRPrinciple2 from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRPrinciple2';
import BRSRPrinciple3 from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRPrinciple3';
import BRSRPrinciple4 from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRPrinciple4';
import BRSRPrinciple5 from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRPrinciple5';
import BRSRPrinciple6 from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRPrinciple6';
import BRSRPrinciple7 from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRPrinciple7';
import BRSRPrinciple8 from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRPrinciple8';
import BRSRPrinciple9 from '../../pages/SubscriberSupplierAdmin/Reporting/BRSR/BRSRPrinciple9';

/**
 * BRSR Report Generator Utility Class
 * Handles generation of BRSR report structure and HTML
 */
export default class BRSRReportGenerator {

  /**
   * Processes response data from new API format (FY keys) into expected format
   * @param {Object} responseData - Response data with FY keys like { "FY2025": [...], "FY2024": [...] }
   * @returns {Object} Processed data in expected format
   */
  static processFYResponseData(responseData) {
    if (!responseData || typeof responseData !== 'object') {
      return {};
    }
    
    // Extract FY keys and sort them (newest first)
    const fyKeys = Object.keys(responseData).filter(key => key.startsWith('FY')).sort((a, b) => {
      const yearA = parseInt(a.replace('FY', ''));
      const yearB = parseInt(b.replace('FY', ''));
      return yearB - yearA; // Descending order (newest first)
    });
    
    // Map FY data to expected format
    const processedData = {};
    fyKeys.forEach((fyKey, index) => {
      const dataArray = responseData[fyKey] || [];
      const dataObj = {};
      dataArray.forEach(d => {
        d.keyValues?.forEach(val => {
          val.value = Utils.roundToDecimalPlacesIfNumber(val.value ?? '');
        });
        dataObj[d.key] = d;
      });
      
      if (index === 0) {
        processedData.currentYear = dataObj;
      } else if (index === 1) {
        processedData.previousYear = dataObj;
      } else if (index === 2) {
        processedData.twoBack = dataObj;
      }
    });
    
    return processedData;
  }

  /**
   * Generates BRSR report structure with all sections and principles
   * @param {Object} params - Generation parameters
   * @param {Object} params.reportClone - Report clone object to populate
   * @param {number} params.regulationId - Regulation ID
   * @param {number} params.yearFilter - Year filter
   * @param {Function} params.t - Translation function
   * @param {Function} params.setReport - Function to set report state
   * @param {Function} params.setLoaderDisplay - Function to set loader display
   * @param {boolean} params.shouldSave - Whether to save the report
   * @param {Function} params.saveBRSRReportStructure - Function to save report structure
   * @returns {Promise<void>}
   */
  static async generateBRSRReportStructure({
    reportClone,
    regulationId,
    yearFilter,
    t,
    setReport,
    setLoaderDisplay,
    shouldSave = false,
    saveBRSRReportStructure
  }) {
    try {
      setLoaderDisplay(true);
      
      // Calculate periodStart and periodEnd from company settings
      // BRSR always uses FINANCIAL_YEAR frequency
      const { defaultPeriodStart, defaultPeriodEnd } = Utils.calculateDefaultPeriods(ReportSettingsFrequency.FINANCIAL_YEAR, yearFilter);
      const periodStart = defaultPeriodStart;
      const periodEnd = defaultPeriodEnd;
      
      // Fetch all BRSR template data using new API
      const pageRequests = [];
      
      // Page 1 needs current, previous, and two years back (offset=2)
      pageRequests.push(
        ReportService.getBRSRReportTemplateBasic({
          pageId: 1,
          type: 'BRSR',
          standardId: regulationId,
          periodStart,
          periodEnd,
          rFreq: 'FINANCIAL_YEAR',
          offset: 3
        })
      );
      
      // Pages 3-10: Pages 6 and 9 need only current year (offset=1), others need current and previous year (offset=2)
      for (let pageId = 2; pageId <= 10; pageId++) {
        // Pages 2, 6, and 9 require only current year data (offset=1)
        const offset = (pageId === 2 || pageId === 6 || pageId === 9) ? 1 : 2;
        pageRequests.push(
          ReportService.getBRSRReportTemplateBasic({
            pageId: pageId,
            type: 'BRSR',
            standardId: regulationId,
            periodStart,
            periodEnd,
            rFreq: 'FINANCIAL_YEAR',
            offset: offset
          })
        );
      }
      
      const templateRes = await Promise.all(pageRequests);
      
      // Process page1 data (current, previous, two years back)
      const p1ResponseData = templateRes[0].data?.data || {};
      const p1Processed = this.processFYResponseData(p1ResponseData);
      const p1Data = p1Processed.currentYear || {};
      const p1PrevData = p1Processed.previousYear || {};
      const p1TwoYearBackData = p1Processed.twoBack || {};
      
      // Process pages 2-10 data
      // Pages 2, 6, 9 only need current year (offset=1)
      // Pages 3, 4, 5, 7, 8, 10 need current and previous year (offset=2)
      const pageData = {};
      for (let pageId = 2; pageId <= 10; pageId++) {
        const pageIndex = pageId - 1; // Adjust index (page 2 is at index 1, etc.)
        const pageResponseData = templateRes[pageIndex].data?.data || {};
        const pageProcessed = this.processFYResponseData(pageResponseData);
        
        // Only include previousYear for pages that need it (3, 4, 5, 7, 8, 10)
        if (pageId === 2 || pageId === 6 || pageId === 9) {
          pageData[pageId] = {
            currentYear: pageProcessed.currentYear || {}
          };
        } else {
          pageData[pageId] = {
            currentYear: pageProcessed.currentYear || {},
            previousYear: pageProcessed.previousYear || {}
          };
        }
      }
      
      // Generate Title Page HTML
      // yearFilter represents the FY end year, so FY is (yearFilter-1)-yearFilter
      const fyStartYear = yearFilter - 1;
      const fyEndYear = yearFilter;
      const titlePageHTML = `
        <div class="ETA_card_center">
          <section class="Section_cover Main_Section">
            <div class="ETA_card_Section front-page">
              <div class="Section_cover_head">
                <h2>BUSINESS RESPONSIBILITY AND SUSTAINABILITY (BRSR) REPORT</h2>
                <h3>FY ${fyStartYear}-${fyEndYear}</h3>
              </div>
            </div>
          </section>
        </div>
      `;
      
      // Generate Section A HTML
      const sectionAHTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRSectionA
            t={t}
            page1={p1Data}
            page1PrevData={{
              oneBack: p1PrevData,
              twoBack: p1TwoYearBackData
            }}
            yearFilter={yearFilter}
          />
        </div>
      );
      
      // Generate Section B HTML
      const sectionBHTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          {/* <BRSRSectionB
            t={t}
            page2={pageData[2]}
            yearFilter={yearFilter}
          /> */}
          <BRSRSectionBGridLayout
            t={t}
            page2={pageData[2]}
          />
        </div>
      );
      
      // Generate Section C HTML
      const sectionCHTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRSectionC t={t} />
        </div>
      );
      
      // Generate Principle 1 HTML
      const principle1HTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRPrinciple1
            t={t}
            page3={pageData[3]}
            yearFilter={yearFilter}
          />
        </div>
      );
      
      // Generate Principle 2 HTML
      const principle2HTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRPrinciple2
            t={t}
            page4={pageData[4]}
            yearFilter={yearFilter}
          />
        </div>
      );
      
      // Generate Principle 3 HTML
      const principle3HTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRPrinciple3
            t={t}
            page5={pageData[5]}
            yearFilter={yearFilter}
          />
        </div>
      );
      
      // Generate Principle 4 HTML
      const principle4HTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRPrinciple4
            t={t}
            page6={pageData[6]}
            yearFilter={yearFilter}
          />
        </div>
      );
      
      // Generate Principle 5 HTML
      const principle5HTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRPrinciple5
            t={t}
            page7={pageData[7]}
            yearFilter={yearFilter}
          />
        </div>
      );
      
      // Generate Principle 6 HTML
      const principle6HTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRPrinciple6
            t={t}
            page8={pageData[8]}
            yearFilter={yearFilter}
          />
        </div>
      );
      
      // Generate Principle 7 HTML
      const principle7HTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRPrinciple7
            t={t}
            page9={pageData[9]}
            yearFilter={yearFilter}
          />
        </div>
      );
      
      // Generate Principle 8 HTML
      const principle8HTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRPrinciple8
            t={t}
            page10={pageData[10]}
            yearFilter={yearFilter}
          />
        </div>
      );
      
      // Generate Principle 9 HTML
      const principle9HTML = renderToStaticMarkup(
        <div className="ETA_card_center">
          <BRSRPrinciple9
            t={t}
            page10={pageData[10]}
            yearFilter={yearFilter}
          />
        </div>
      );
      
      // Add Title Section
      reportClone.sections.push({
        name: "Title",
        index: 0,
        sortOrder: 1,
        type: ReportSection.TITLE,
        pages: [{
          name: "Title",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.TITLE,
          html: titlePageHTML,
          json: null
        }]
      });
      
      // Add Section A
      reportClone.sections.push({
        name: "SECTION A",
        index: 1,
        sortOrder: 2,
        type: ReportSection.SECTION,
        pages: [{
          name: "SECTION A: GENERAL DISCLOSURES",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.SECTION,
          html: sectionAHTML,
          json: null
        }]
      });
      
      // Add Section B
      reportClone.sections.push({
        name: "SECTION B",
        index: 2,
        sortOrder: 3,
        type: ReportSection.SECTION,
        pages: [{
          name: "SECTION B: MANAGEMENT AND PROCESS DISCLOSURES",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.SECTION,
          html: sectionBHTML,
          json: null
        }]
      });
      
      // Add Section C
      reportClone.sections.push({
        name: "SECTION C",
        index: 3,
        sortOrder: 4,
        type: ReportSection.SECTION,
        pages: [{
          name: "SECTION C: PRINCIPLE WISE PERFORMANCE DISCLOSURE",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.SECTION,
          html: sectionCHTML,
          json: null
        }]
      });
      
      // Add Principle 1
      reportClone.sections.push({
        name: "PRINCIPLE 1",
        index: 4,
        sortOrder: 5,
        type: ReportSection.PRINCIPLE,
        pages: [{
          name: "PRINCIPLE 1: Ethical, Transparent and Accountable",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.PRINCIPLE,
          html: principle1HTML,
          json: null
        }]
      });
      
      // Add Principle 2
      reportClone.sections.push({
        name: "PRINCIPLE 2",
        index: 5,
        sortOrder: 6,
        type: ReportSection.PRINCIPLE,
        pages: [{
          name: "PRINCIPLE 2: Sustainable and Safe",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.PRINCIPLE,
          html: principle2HTML,
          json: null
        }]
      });
      
      // Add Principle 3
      reportClone.sections.push({
        name: "PRINCIPLE 3",
        index: 6,
        sortOrder: 7,
        type: ReportSection.PRINCIPLE,
        pages: [{
          name: "PRINCIPLE 3: Respect and Promote Well-being of Employees",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.PRINCIPLE,
          html: principle3HTML,
          json: null
        }]
      });
      
      // Add Principle 4
      reportClone.sections.push({
        name: "PRINCIPLE 4",
        index: 7,
        sortOrder: 8,
        type: ReportSection.PRINCIPLE,
        pages: [{
          name: "PRINCIPLE 4: Respect Stakeholders",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.PRINCIPLE,
          html: principle4HTML,
          json: null
        }]
      });
      
      // Add Principle 5
      reportClone.sections.push({
        name: "PRINCIPLE 5",
        index: 8,
        sortOrder: 9,
        type: ReportSection.PRINCIPLE,
        pages: [{
          name: "PRINCIPLE 5: Respect and Promote Human Rights",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.PRINCIPLE,
          html: principle5HTML,
          json: null
        }]
      });
      
      // Add Principle 6
      reportClone.sections.push({
        name: "PRINCIPLE 6",
        index: 9,
        sortOrder: 10,
        type: ReportSection.PRINCIPLE,
        pages: [{
          name: "PRINCIPLE 6: Protect and Restore Environment",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.PRINCIPLE,
          html: principle6HTML,
          json: null
        }]
      });
      
      // Add Principle 7
      reportClone.sections.push({
        name: "PRINCIPLE 7",
        index: 10,
        sortOrder: 11,
        type: ReportSection.PRINCIPLE,
        pages: [{
          name: "PRINCIPLE 7: Responsible Public Policy",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.PRINCIPLE,
          html: principle7HTML,
          json: null
        }]
      });
      
      // Add Principle 8
      reportClone.sections.push({
        name: "PRINCIPLE 8",
        index: 11,
        sortOrder: 12,
        type: ReportSection.PRINCIPLE,
        pages: [{
          name: "PRINCIPLE 8: Inclusive Growth",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.PRINCIPLE,
          html: principle8HTML,
          json: null
        }]
      });
      
      // Add Principle 9
      reportClone.sections.push({
        name: "PRINCIPLE 9",
        index: 12,
        sortOrder: 13,
        type: ReportSection.PRINCIPLE,
        pages: [{
          name: "PRINCIPLE 9: Consumer Value",
          sortOrder: 1,
          index: 0,
          type: ReportPageType.PRINCIPLE,
          html: principle9HTML,
          json: null
        }]
      });
      
      setReport(reportClone);
      setLoaderDisplay(false);
      
      // Save the generated structure to backend if needed
      if (shouldSave && reportClone.id && saveBRSRReportStructure) {
        await saveBRSRReportStructure(reportClone);
      }
    } catch (error) {
      setLoaderDisplay(false);
      throw error;
    }
  }
}
